import {
   presetStaticComponent,
   StructureRepresentationPresetProvider,
} from 'molstar/lib/mol-plugin-state/builder/structure/representation-preset';
import { StateObjectRef } from 'molstar/lib/mol-state';
import CommonParams = StructureRepresentationPresetProvider.CommonParams;
import reprBuilder = StructureRepresentationPresetProvider.reprBuilder;
import updateFocusRepr = StructureRepresentationPresetProvider.updateFocusRepr;

/**
 * Custom Molstar structure representation preset that mirrors the built-in
 * 'Polymer & Ligand' preset but intentionally omits the water component.
 *
 * Purpose: Molstar's default presets always create a (semi-transparent) water
 * representation which adds visual noise, especially for large crystal structures.
 * This preset skips it entirely so water can be toggled back on demand via
 * `AhojMolstarWrapper.toggleWater()`.
 *
 * Registered with the plugin alongside `ViewerAutoPreset` during `init()` and
 * set as the default via `PluginConfig.Structure.DefaultRepresentationPreset`.
 */
export const NoWaterPreset = StructureRepresentationPresetProvider({
   id: 'preset-structure-representation-polymer-and-ligand-no-waters',
   display: {
      name: 'Polymer & Ligand (No Waters)',
      group: 'Ahoj Presets',
      description:
         'Shows polymers as Cartoon, ligands as Ball & Stick, carbohydrates as 3D-SNFG and water molecules semi-transparent.',
   },
   params: () => CommonParams,
   async apply(ref, params, plugin) {
      const structureCell = StateObjectRef.resolveAndCheck(plugin.state.data, ref);
      if (!structureCell) return {};

      const components = {
         polymer: await presetStaticComponent(plugin, structureCell, 'polymer'),
         ligand: await presetStaticComponent(plugin, structureCell, 'ligand'),
         nonStandard: await presetStaticComponent(plugin, structureCell, 'non-standard'),
         branched: await presetStaticComponent(plugin, structureCell, 'branched', { label: 'Carbohydrate' }),
         ion: await presetStaticComponent(plugin, structureCell, 'ion'),
         lipid: await presetStaticComponent(plugin, structureCell, 'lipid'),
         coarse: await presetStaticComponent(plugin, structureCell, 'coarse'),
      };

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const structure = structureCell.obj!.data;
      const cartoonProps = {
         sizeFactor: structure.isCoarseGrained ? 0.8 : 0.2,
      };

      // TODO make configurable
      const lipidType = (components.lipid?.obj?.data?.elementCount || 0) > 20_000 ? 'line' : 'ball-and-stick';

      const { update, builder, typeParams, color, symmetryColor, ballAndStickColor } = reprBuilder(
         plugin,
         params,
         structure,
      );

      const representations = {
         polymer: builder.buildRepresentation(
            update,
            components.polymer,
            { type: 'cartoon', typeParams: { ...typeParams, ...cartoonProps }, color: symmetryColor },
            { tag: 'polymer' },
         ),
         ligand: builder.buildRepresentation(
            update,
            components.ligand,
            { type: 'ball-and-stick', typeParams, color, colorParams: ballAndStickColor },
            { tag: 'ligand' },
         ),
         nonStandard: builder.buildRepresentation(
            update,
            components.nonStandard,
            { type: 'ball-and-stick', typeParams, color, colorParams: ballAndStickColor },
            { tag: 'non-standard' },
         ),
         branchedBallAndStick: builder.buildRepresentation(
            update,
            components.branched,
            {
               type: 'ball-and-stick',
               typeParams: { ...typeParams, alpha: 0.3 },
               color,
               colorParams: ballAndStickColor,
            },
            { tag: 'branched-ball-and-stick' },
         ),
         branchedSnfg3d: builder.buildRepresentation(
            update,
            components.branched,
            { type: 'carbohydrate', typeParams, color },
            { tag: 'branched-snfg-3d' },
         ),
         ion: builder.buildRepresentation(
            update,
            components.ion,
            {
               type: 'ball-and-stick',
               typeParams,
               color,
               colorParams: { carbonColor: { name: 'element-symbol', params: {} } },
            },
            { tag: 'ion' },
         ),
         lipid: builder.buildRepresentation(
            update,
            components.lipid,
            {
               type: lipidType,
               typeParams: { ...typeParams, alpha: 0.6, visuals: lipidType === 'line' ? ['intra-bond'] : undefined },
               color,
               colorParams: { carbonColor: { name: 'element-symbol', params: {} } },
            },
            { tag: 'lipid' },
         ),
         coarse: builder.buildRepresentation(
            update,
            components.coarse,
            { type: 'spacefill', typeParams, color: color || 'chain-id' },
            { tag: 'coarse' },
         ),
      };

      await update.commit({ revertOnError: false });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      await updateFocusRepr(plugin, structure, params.theme?.focus?.name, params.theme?.focus?.params);
      return { components, representations };
   },
});
